import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Save, Upload, Lock, ArrowLeft, Users, ShieldCheck } from 'lucide-react'
import AppIcon from '@/components/common/AppIcon'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { getTables } from '@/api/designers'
import { getAppTypes, type AppTypeInfo } from '@/api/journey'
import { DEFAULT_BIZ_LOGIC } from '@/api/projectDb'
import type { RawAppData } from '@/types'
import type { AppShellSettings } from '@twasta/page-runtime/shell'
import ShellLayoutSettings from '../ShellLayoutSettings'
import { BizLogicSelector, CleanupWorkspaceButton, DbModeSelector, DownloadRuntimeButton, RadioCard } from './SettingsControls'
import { ChangeAppIdButton, DeleteAppButton } from './DangerZone'
import '../simple/simple.css'

/**
 * App settings — today's Application Properties, regrouped (plan §3.3, §9):
 *
 *   About this app   App type (first), name, group, description, icon, colour,
 *                    how people move around the running app (Run layouts)
 *   Built with       database + business-logic language (locked once tables exist)
 *   Sign-in …        a summary; People and Advanced › Sign-in and rights rules
 *                    hold the editors
 *   Technical        (collapsed) Application ID, data isolation, document
 *                    store, runtime download, workspace clean-up, delete
 *
 * Saves through the same endpoint as before (the caller's `onSave`), so every
 * field the old form wrote is written exactly the same way.
 */

const THEME_COLORS = ['#3F51B5', '#4CAF50', '#FF5722', '#009688', '#E91E63']

interface AppSettingsFormProps {
  appData: RawAppData
  onSave: (app: RawAppData['application']) => void | Promise<void>
  /** When false, the user lacks UPDATE_PROPERTIES — hide Save, show a badge. */
  canEdit?: boolean
  saving?: boolean
  /** Nothing built yet: show "Back to the plan". */
  onBackToPlan?: () => void
  onOpenPeople?: () => void
  onOpenRightsRules?: () => void
}

function AuthSummary({ auth }: { auth?: RawAppData['application']['auth'] }) {
  const a = (auth || {}) as { providers?: string[]; requireLogin?: boolean; allowRegistration?: boolean; mfa?: { mode?: string }; grants?: unknown[] }
  const parts: string[] = []
  parts.push(a.requireLogin ? 'People must sign in' : 'Signing in is not required for public pages')
  if ((a.providers || []).length) parts.push(`signs in with ${(a.providers || []).join(', ').replace('project-users', 'email and password')}`)
  if (a.mfa?.mode === 'required') parts.push('two-step sign-in required')
  else if (a.mfa?.mode !== 'off') parts.push('two-step sign-in optional')
  if (a.allowRegistration) parts.push('visitors may create their own account')
  if ((a.grants || []).length) parts.push(`${(a.grants || []).length} rights rules`)
  return <span>{parts.join(' · ')}.</span>
}

export function AppSettingsForm({
  appData, onSave, canEdit = true, saving = false, onBackToPlan, onOpenPeople, onOpenRightsRules,
}: AppSettingsFormProps) {
  const navigate = useNavigate()
  const [form, setForm] = useState({ ...appData.application })
  const [customColor, setCustomColor] = useState(form.theme_color || '#3F51B5')
  const [appTypes, setAppTypes] = useState<AppTypeInfo[]>([])
  const [defaultType, setDefaultType] = useState('transaction')

  useEffect(() => {
    getAppTypes()
      .then((r) => { setAppTypes(r.types || []); setDefaultType(r.default || 'transaction') })
      .catch((err) => { console.error('[settings] app types unavailable', err); setAppTypes([]) })
  }, [])

  // Once the project has any generated database object (a designed table), the
  // database engine is baked into the schema + business logic, so it can't be
  // changed safely from here — switching must go through the Database
  // Designer's "Change database" flow (which runs a guided migration).
  const folderName = form.folder_name
  const isDraft = !folderName || folderName === 'Draft Application'
  const [dbLocked, setDbLocked] = useState(false)
  useEffect(() => {
    if (isDraft) { setDbLocked(false); return }
    let cancelled = false
    getTables(folderName)
      .then((data) => { if (!cancelled) setDbLocked((data?.tables?.length ?? 0) > 0) })
      .catch(() => { if (!cancelled) setDbLocked(false) })
    return () => { cancelled = true }
  }, [folderName, isDraft])

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  // ── Image icon upload ──
  // The uploaded image is downscaled to a small PNG data URL and stored on
  // the application object (`icon_image_data`), so it persists inside
  // application.json with no extra endpoint. When set, it is shown in
  // place of the material font icon; the font icon remains the fallback.
  const iconFileRef = useRef<HTMLInputElement>(null)
  const handleIconImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (PNG, JPG, SVG, …)')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB')
      return
    }
    const reader = new FileReader()
    reader.onerror = () => toast.error('Could not read the image file')
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => toast.error('Could not load the image — is it a valid image file?')
      img.onload = () => {
        try {
          const MAX = 128
          const scale = Math.min(1, MAX / Math.max(img.width, img.height))
          const w = Math.max(1, Math.round(img.width * scale))
          const h = Math.max(1, Math.round(img.height * scale))
          const canvas = document.createElement('canvas')
          canvas.width = w
          canvas.height = h
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('canvas 2d context unavailable')
          ctx.drawImage(img, 0, 0, w, h)
          handleChange('icon_image_data', canvas.toDataURL('image/png'))
        } catch {
          toast.error('Could not process the image')
        }
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  }

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)', background: 'var(--color-input-bg)', color: 'var(--color-text)',
    fontSize: '0.9375rem', fontFamily: 'var(--font-primary)', boxSizing: 'border-box',
  }
  const currentType = form.app_type && appTypes.some((t) => t.id === form.app_type) ? form.app_type : defaultType

  // Data isolation (Postgres only): schema-per-project vs shared. The data
  // DATABASE is always per-tenant (twasta_preview_<tenant>); this only picks
  // the schema INSIDE it.
  const sanitize = (v?: string) =>
    ((v || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'project').slice(0, 50)
  const isolation: 'shared' | 'own' | 'external' =
    (form.data_isolation as 'shared' | 'own' | 'external' | undefined) || (form.preview_schema_name ? 'own' : 'shared')
  const autoSchema = sanitize(form.folder_name || form.title)
  // Where the tables go is picked before the app is built. Once it has tables,
  // a change starts it in an empty schema and leaves the existing data behind.
  const savedIsolation = (appData.application.data_isolation as string | undefined)
    || (appData.application.preview_schema_name ? 'own' : 'shared')
  const storageChanged = isolation !== savedIsolation
    || (isolation === 'own' && (form.preview_schema_name || '') !== (appData.application.preview_schema_name || ''))

  return (
    <div className="swb" style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fadeIn 0.25s ease-out' }}>
      {onBackToPlan && (
        <div className="swb-notice swb-notice-info">
          <span style={{ flex: '1 1 300px', fontSize: '0.875rem' }}>
            Nothing is built yet, so everything here can still be changed.
          </span>
          <button type="button" className="swb-btn swb-btn-quiet" onClick={onBackToPlan}>
            <ArrowLeft size={14} /> Back to the plan
          </button>
        </div>
      )}

      {/* ── About this app ── */}
      <section className="swb-card swb-card-pad" aria-labelledby="set-about" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 id="set-about" className="swb-section-title">About this app</h2>

        <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <legend className="swb-label" style={{ padding: 0, marginBottom: 6 }}>App type</legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
            {appTypes.map((t) => (
              <RadioCard
                key={t.id}
                value={t.id}
                current={currentType}
                onSelect={(v) => setForm((prev) => ({ ...prev, app_type: v }))}
                disabled={!canEdit}
                title={t.label}
                blurb={t.line}
                badge={t.id === defaultType && !form.app_type ? 'default' : undefined}
              />
            ))}
          </div>
          <span className="swb-muted">
            Picks the steps and tabs the Workbench shows you. Changing it never changes what is built.
          </span>
        </fieldset>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          <div className="swb-field">
            <label htmlFor="set-name">Name</label>
            <input id="set-name" value={form.title} onChange={(e) => handleChange('title', e.target.value)} style={fieldStyle} />
          </div>
          <div className="swb-field">
            <label htmlFor="set-group">Group</label>
            <input id="set-group" value={form.group || ''} onChange={(e) => handleChange('group', e.target.value)} style={fieldStyle} />
          </div>
        </div>
        <div className="swb-field">
          <label htmlFor="set-desc">Description</label>
          <textarea
            id="set-desc"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 32px', alignItems: 'flex-end' }}>
          <div className="swb-field" style={{ minWidth: 260 }}>
            <label htmlFor="set-icon">Icon</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AppIcon
                icon={form.icon}
                imageData={form.icon_image_data}
                size={40}
                iconSize={24}
                color="#fff"
                background={form.theme_color || 'var(--color-accent)'}
                borderRadius="10px"
              />
              <input id="set-icon" value={form.icon} onChange={(e) => handleChange('icon', e.target.value)}
                style={{ ...fieldStyle, flex: 1 }} title="A Material icon name, for example receipt_long" />
              <input ref={iconFileRef} type="file" accept="image/*" onChange={handleIconImageUpload} style={{ display: 'none' }} />
              <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm"
                onClick={() => iconFileRef.current?.click()}
                title="Upload an image to use as the app icon instead of the font icon">
                <Upload size={14} /> Image
              </button>
            </div>
            {form.icon_image_data && (
              <button type="button" className="swb-link" style={{ alignSelf: 'flex-start', fontWeight: 500, fontSize: '0.8125rem' }}
                onClick={() => handleChange('icon_image_data', '')}>
                Remove image — use the font icon
              </button>
            )}
          </div>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="swb-label" style={{ padding: 0, marginBottom: 6 }}>Colour</legend>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {THEME_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Colour ${color}`}
                  aria-pressed={form.theme_color === color}
                  onClick={() => { handleChange('theme_color', color); setCustomColor(color) }}
                  style={{
                    width: 34, height: 34, borderRadius: 17, background: color, cursor: 'pointer',
                    border: form.theme_color === color ? '3px solid var(--color-bg-secondary)' : '2px solid transparent',
                    boxShadow: form.theme_color === color ? `0 0 0 2px ${color}` : 'none',
                  }}
                />
              ))}
              <input
                type="color"
                aria-label="Another colour"
                value={customColor}
                onChange={(e) => { setCustomColor(e.target.value); handleChange('theme_color', e.target.value) }}
                style={{ width: 34, height: 34, padding: 0, border: 'none', cursor: 'pointer', borderRadius: '50%' }}
              />
            </div>
          </fieldset>
        </div>

        {/* Run layouts (application.shell) — how people move around the running app. */}
        <ShellLayoutSettings
          value={(form as { shell?: AppShellSettings }).shell}
          disabled={!canEdit}
          onChange={(shell) => setForm((prev) => ({ ...prev, shell }))}
        />
      </section>

      {/* ── Built with ── */}
      <section className="swb-card swb-card-pad" aria-labelledby="set-built" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <h2 id="set-built" className="swb-section-title">Built with</h2>
          <p className="swb-muted" style={{ margin: '4px 0 0', maxWidth: 680 }}>
            Where your app keeps its data, and the language its rules are written in. The AI picks
            these when it writes the plan, and most apps never change them.
          </p>
        </div>
        {dbLocked && (
          <div className="swb-notice swb-notice-plain" style={{ padding: '10px 14px', fontSize: '0.875rem' }}>
            <Lock size={16} style={{ flex: 'none' }} />
            <span style={{ flex: '1 1 300px' }}>
              The database is fixed now that your app has its data tables. To switch databases (with a
              guided migration of your tables and business logic), use{' '}
              <button type="button" className="swb-link"
                onClick={() => navigate(`/designers/database/${encodeURIComponent(folderName)}`)}>
                Advanced › Database › Change database
              </button>.
            </span>
          </div>
        )}
        <DbModeSelector
          value={(form.db_mode as 'postgres' | 'sqlite' | 'oracle') || 'postgres'}
          disabled={dbLocked}
          onChange={(mode) => {
            setForm((prev) => ({
              ...prev,
              db_mode: mode,
              // Keep biz_logic inside the validity matrix:
              // SQLite → Python only; Oracle → PL/SQL (or Python, or
              // Java on the J2EE runtime); Postgres → the default
              // (Python) when leaving an Oracle/plsql state.
              biz_logic:
                mode === 'sqlite'
                  ? 'python'
                  : mode === 'oracle'
                    ? (prev.biz_logic === 'python' || prev.biz_logic === 'java' ? prev.biz_logic : 'plsql')
                    : (prev.biz_logic === 'plsql' ? DEFAULT_BIZ_LOGIC.postgres : prev.biz_logic),
            }))
          }}
        />
        <BizLogicSelector
          value={(form.biz_logic as 'plpgsql' | 'plsql' | 'python' | 'java') || 'python'}
          dbMode={(form.db_mode as 'postgres' | 'sqlite' | 'oracle') || 'postgres'}
          onChange={(lang) => handleChange('biz_logic', lang)}
        />
      </section>

      {/* ── Sign-in and who can do what ── */}
      <section className="swb-card swb-card-pad" aria-labelledby="set-access"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <span style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <h2 id="set-access" className="swb-section-title">Sign-in and who can do what</h2>
          <span style={{ fontSize: '0.875rem' }}><AuthSummary auth={form.auth} /></span>
          <span className="swb-muted">Managed with the people themselves, including other organizations.</span>
        </span>
        {onOpenPeople && (
          <button type="button" className="swb-btn swb-btn-secondary" onClick={onOpenPeople}>
            <Users size={15} /> Open People
          </button>
        )}
        {onOpenRightsRules && (
          <button type="button" className="swb-btn swb-btn-secondary" onClick={onOpenRightsRules}>
            <ShieldCheck size={15} /> Sign-in and rights rules
          </button>
        )}
      </section>

      {/* ── Technical details (collapsed) ── */}
      <details className="swb-card" style={{ padding: '0 20px' }}>
        <summary style={{ cursor: 'pointer', minHeight: 56, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px' }}>
          <span style={{ fontSize: '1rem', fontWeight: 600 }}>Technical details</span>
          <span className="swb-muted">App ID, data isolation, file storage, runtime download</span>
        </summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--color-surface-2)' }}>
            <span style={{ flex: '0 0 200px', fontWeight: 600, fontSize: '0.875rem' }}>Application ID</span>
            <span style={{ flex: '1 1 260px', fontSize: '0.875rem' }}>
              <code style={{ fontFamily: 'var(--font-mono)' }}>{form.id || form.folder_name}</code>
              <span className="swb-muted"> · set when the app was created</span>
            </span>
            <ChangeAppIdButton folderName={form.folder_name} currentId={form.id || ''} />
          </div>

          {(form.db_mode || 'postgres') === 'postgres' && (
            <div style={{ display: 'grid', gap: 10, paddingTop: 12, borderTop: '1px solid var(--color-surface-2)' }}>
              <div className="swb-field">
                <label htmlFor="set-iso">Data isolation</label>
                <select
                  id="set-iso"
                  value={isolation}
                  onChange={(e) => {
                    const next = e.target.value as 'shared' | 'own' | 'external'
                    setForm((prev) => ({
                      ...prev,
                      data_isolation: next,
                      // Returning to shared drops any manual schema so it falls
                      // back to the common `twasta` schema.
                      preview_schema_name: next === 'shared' ? '' : prev.preview_schema_name,
                    }))
                  }}
                  style={fieldStyle}
                >
                  <option value="shared">Shared schema — projects share “twasta” in the tenant database</option>
                  <option value="own">Own schema — this project gets its own schema (default for new apps)</option>
                  <option value="external">External database schema — set by the project’s database connection</option>
                </select>
                <span className="swb-muted">
                  {isolation === 'external'
                    ? 'The project runs on the database connection selected in Database Designer → Change database; the schema below is that connection’s Schema setting.'
                    : <>The data database is always per-tenant (<code>twasta_preview_&lt;tenant&gt;</code>); this only picks the schema inside it. Sharing a project across tenants never shares data.</>}
                </span>
              </div>
              {dbLocked && storageChanged && isolation !== 'external' && (
                <div className="swb-notice swb-notice-warn" role="alert" style={{ fontSize: '0.875rem' }}>
                  This app already has tables. After saving, it will use a different schema and start
                  empty there — the existing tables and their records stay where they are.
                </div>
              )}
              {isolation === 'external' && (
                <div className="swb-field">
                  <label htmlFor="set-schema-ext">Schema (from the database connection)</label>
                  <input id="set-schema-ext" type="text" value={form.preview_schema_name || ''} readOnly disabled
                    placeholder="(no schema set on the connection — defaults to twasta)" style={{ ...fieldStyle, opacity: 0.75 }} />
                </div>
              )}
              {isolation === 'own' && (
                <div className="swb-field">
                  <label htmlFor="set-schema">Schema name</label>
                  <input id="set-schema" type="text" value={form.preview_schema_name || ''}
                    onChange={(e) => handleChange('preview_schema_name', e.target.value)}
                    placeholder={autoSchema} style={fieldStyle} />
                  <span className="swb-muted">Leave blank to auto-name from the project (<code>{autoSchema}</code>).</span>
                </div>
              )}
            </div>
          )}

          {/* Document Store Folder (optional) — backs the image/document upload
              widgets on transaction forms. Separate from project assets. */}
          <div className="swb-field" style={{ paddingTop: 12, borderTop: '1px solid var(--color-surface-2)' }}>
            <label htmlFor="set-docs">Document store folder</label>
            <input id="set-docs" type="text" value={form.document_store_folder || ''}
              onChange={(e) => handleChange('document_store_folder', e.target.value)}
              placeholder="Document_Store" style={fieldStyle} />
            <span className="swb-muted">
              Optional. Where files uploaded through transaction image/document fields are stored, kept
              separate from project assets. Leave blank for the default <code>Document_Store</code> folder
              inside the project. A relative path is resolved inside the project; an absolute path is
              namespaced per tenant and project.
            </span>
          </div>

          <DownloadRuntimeButton folderName={form.folder_name} disabled={isDraft} />
          <CleanupWorkspaceButton folderName={form.folder_name} disabled={isDraft} />
          <DeleteAppButton folderName={form.folder_name} />
        </div>
      </details>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, position: 'sticky', bottom: 0, padding: '12px 0', background: 'var(--color-bg)' }}>
        {canEdit ? (
          <button type="button" className="swb-btn swb-btn-primary swb-btn-lg" disabled={saving} onClick={() => { void onSave(form) }}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        ) : (
          <ReadOnlyBadge />
        )}
      </div>
    </div>
  )
}

/** The old name, kept for callers that still say Application Properties. */
export const AppPropertiesForm = AppSettingsForm
